import { FC, useEffect, useState, useMemo, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { FormikHelpers } from "formik";
import { Table } from "@vilocnv/allsetra-core";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";
import { NoDataIcon } from "assets/icons";

// Data
import {
  useAppDispatch,
  useAppSelector,
  useReportCommonStates,
  useCurrentUser
} from "hooks";
import {
  convertDatesForFilterPayload,
  getStandardizedReportDates,
  transformApiDataToHierarchical,
  extractApiData,
  generateReportFormInitialValues,
  generateReportFormValidationSchema
} from "app/data/helpers";
import { setReportsFilterData, setReportsDateValues } from "app/features";
import { useTranslation } from "react-i18next";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import { selectReportsState } from "app/data/selectors";
import { generateReportThunk } from "app/features";
import GenerateReportForm from "components/forms/Reports/GenerateReportForm";
import {
  EXCEPTION_REPORT_TABLE_COLUMNS,
  ExceptionReportVehicleExpandableRow
} from "app/data/constants/Reports/exceptionReportsConstants";

const DEFAULT_FILTER_VALUES = {
  driver: [],
  group: [],
  object: [],
  objectType: [],
  ridesModes: []
};

const ExceptionReports: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    filterOpen,
    setFilterOpen,
    dateValues,
    setDateValues,
    handleObjectsSearch,
    handleAccountSearch,
    selectedLocalObjects,
    handleOnChangeReportFilterForm,
    preloadedObjects,
    searchedMinimalAccounts,
    searchedMinimalAccountsLoading
  } = useReportCommonStates();

  const { report, loading: reportsLoading } =
    useAppSelector(selectReportsState);

  const [filterValues, setFilterValues] = useState<any>(DEFAULT_FILTER_VALUES);
  const [exceptionReportData, setExceptionReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const lastRequestRef = useRef<string>("");

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const tableColumns = useMemo(() => EXCEPTION_REPORT_TABLE_COLUMNS(t), [t]);

  const handleDateChange = (value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  };

  const handleDateRangeSubmit = () => {
    if (
      dateValues?.startDate &&
      dateValues?.endDate &&
      filterValues.object?.length > 0
    ) {
      const requestKey = `${dateValues.startDate}-${dateValues.endDate}-${JSON.stringify(filterValues)}`;

      if (lastRequestRef.current === requestKey) {
        return;
      }

      lastRequestRef.current = requestKey;
      fetchReportData(dateValues);
    }
  };

  const fetchReportData = async (dateRange?: any) => {
    setLoading(true);

    const currentDateValues = dateRange || dateValues;
    const { formattedStartDate, formattedEndDate } =
      convertDatesForFilterPayload(currentDateValues);

    const { daysOfWeek, startTime, endTime, ...restFilterValues } =
      filterValues;

    const payload = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...restFilterValues,
      object: filterValues.assignedToAllObjects ? [] : filterValues.object,
      reportType: 9,
      shouldNotResolveAddresses: true
    };

    try {
      const result = await dispatch(generateReportThunk(payload));

      if (result.type === "reports/generateReportThunk/fulfilled") {
        dispatch(setReportsFilterData(payload));
      } else if (result.type === "reports/generateReportThunk/rejected") {
        setExceptionReportData([]);
      }
    } catch (error) {
      setExceptionReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (values: any, formikHelpers: any) => {
    formikHelpers.setSubmitting(true);

    setFilterValues(values);

    const requestKey = `${dateValues.startDate}-${dateValues.endDate}-${JSON.stringify(values)}`;

    if (lastRequestRef.current === requestKey) {
      formikHelpers.setSubmitting(false);
      return;
    }

    lastRequestRef.current = requestKey;

    const currentDateValues = dateValues;
    const { formattedStartDate, formattedEndDate } =
      convertDatesForFilterPayload(currentDateValues);

    const { daysOfWeek, startTime, endTime, ...restValues } = values;

    const payload = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...restValues,
      object: values.assignedToAllObjects ? [] : values.object,
      reportType: 9,
      shouldNotResolveAddresses: true
    };

    try {
      const result = await dispatch(generateReportThunk(payload));

      if (result.type === "reports/generateReportThunk/fulfilled") {
        dispatch(setReportsFilterData(payload));
        formikHelpers.setSubmitting(false);
        setFilterOpen(false);
      } else if (result.type === "reports/generateReportThunk/rejected") {
        setExceptionReportData([]);
        formikHelpers.setSubmitting(false);
      }
    } catch (error) {
      setExceptionReportData([]);
      formikHelpers.setSubmitting(false);
    }
  };

  const handleLoadReport = () => {
    const { formattedStartDate, formattedEndDate } =
      convertDatesForFilterPayload(dateValues);

    const { daysOfWeek, startTime, endTime, ...restFilterValues } =
      filterValues;

    const payload = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...restFilterValues,
      object: filterValues.assignedToAllObjects ? [] : filterValues.object,
      reportType: 9,
      shouldNotResolveAddresses: true
    };

    dispatch(setReportsFilterData(payload));
    dispatch(generateReportThunk(payload));
  };

  useEffect(() => {
    const initialDateRange = {
      startDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
      endDate: new Date()
    };
    const formattedInitialRange = getStandardizedReportDates(initialDateRange);

    setDateValues(formattedInitialRange);
    dispatch(setReportsDateValues(formattedInitialRange));
  }, []);

  useEffect(() => {
    if (report) {
      const apiData = extractApiData(report);

      if (apiData.length > 0) {
        const transformedData = transformApiDataToHierarchical(apiData);
        setExceptionReportData(transformedData);
      } else {
        setExceptionReportData([]);
      }
    } else {
      setExceptionReportData([]);
    }
  }, [report]);

  return (
    <Box>
      <ReportsTopbar dropdownTitle={t("reports.exceptionReport")} />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat="daily"
          setDateFormat={() => {}}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={loading}
          handleLoadReport={handleLoadReport}
          disableExportButton={!exceptionReportData?.length}
          reportType={9}
          reportExportTypeValue={2}
          openModal={openModal}
          setOpenModal={setOpenModal}
          exportModalButton={true}
          hideExportButton={true}
          hideDateFormats={true}
        />
        <Box
          sx={{
            border: "1px solid #D5D9E8",
            borderRadius: "10px",
            overflow: "hidden",
            width: "100%",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Table
            columns={tableColumns}
            data={exceptionReportData}
            progressPending={loading || reportsLoading}
            expandableRows
            expandOnRowClicked
            expandableRowsComponent={({ data }: { data: any }) => (
              <ExceptionReportVehicleExpandableRow data={data} />
            )}
            pagination={false}
            headerPadding="50px"
            translator={t}
            sx={{
              border: "none",
              borderRadius: 0,
              "& .rdt_TableWrapper": {
                overflowX: "auto",
                minWidth: "100%"
              },
              "& .rdt_Table": {
                minWidth: "1000px",
                width: "100%"
              },
              "& .rdt_TableHead": {
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "#EFF4FF"
              },
              "& .rdt_TableHeadRow": {
                backgroundColor: "#EFF4FF",
                borderBottom: "1px solid #e0e0e0",
                minHeight: "32px !important"
              },
              "& .rdt_TableRow": {
                backgroundColor: "#F9FBFF"
              },
              "& .rdt_Pagination": {
                display: "none"
              },
              "& .rdt_ExpanderRow": {
                backgroundColor: "#f8f9fa !important",
                paddingLeft: "0 !important",

                "& > td": {
                  padding: "0 !important",
                  backgroundColor: "#f8f9fa !important"
                },
                "& .rdt_Table": {
                  backgroundColor: "#f8f9fa !important",
                  width: "100% !important"
                },
                "& .rdt_TableRow": {
                  backgroundColor: "#EFF4FF !important",
                  "& > div": {
                    flex: "0 0 64px"
                  },
                  "& > div > button > svg": {
                    marginLeft: "36px !important"
                  }
                },
                "& .rdt_TableCell": {
                  backgroundColor: "#EFF4FF !important"
                }
              }
            }}
            noDataComponent={
              !(loading || reportsLoading) &&
              (!exceptionReportData || exceptionReportData.length === 0) ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    padding: "40px",
                    textAlign: "center",
                    width: "100%"
                  }}
                >
                  <Box
                    sx={{
                      width: "200px",
                      height: "200px",
                      marginBottom: "24px"
                    }}
                  >
                    <NoDataIcon />
                  </Box>
                  <Typography
                    sx={{
                      color: "#323947",
                      fontWeight: 500,
                      fontSize: "16px",
                      marginBottom: "8px"
                    }}
                  >
                    {t("common.noDataAvailableYet")}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#646D7B",
                      fontSize: "14px"
                    }}
                  >
                    No exception rides found for the selected period.
                  </Typography>
                </Box>
              ) : null
            }
          />
        </Box>
        <GenerateReportForm
          fetchOnDebounce={handleObjectsSearch}
          fetchAccountOnDebounce={handleAccountSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={handleGenerateReport}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={
            selectedLocalObjects?.length
              ? selectedLocalObjects
              : preloadedObjects
          }
          keys={[]}
          dataLoading={objectsLoading || objectTypesLoading}
          theme={theme}
          onChange={() => handleOnChangeReportFilterForm({})}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          dateRangeLoading={loading}
          reportType={9}
          searchedMinimalAccounts={searchedMinimalAccounts}
          searchedMinimalAccountsLoading={searchedMinimalAccountsLoading}
          formInitialValues={generateReportFormInitialValues}
          formValidations={generateReportFormValidationSchema}
        />
      </Box>
    </Box>
  );
};

export default ExceptionReports;
